import { useState, useEffect, useId } from "react";
import { Button, InputBase, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as UserIcon } from "../../../assets/user.icon.svg";
import { UserRole } from "../../../common/enums/user-role.enum";
import { ModalTemplate } from "../../modal-template/modal-template.comp";
import { DropdownMenuType, SelectionFilter } from "../../menu-filter/menu-filter.comp";
import { KBMultiSelect, KBOption } from "./kb-multi-select.comp";

export const AddUsersModal = ({
    open,
    knowledgeBases,
    initialEmails = [],
    handleClose,
    handleAction
}: {
    open: boolean;
    knowledgeBases: KBOption[];
    initialEmails?: string[];
    handleClose: () => void;
    handleAction: (emails: string[], kbs: KBOption[], role: string) => void;
}) => {
    const [emailText, setEmailText] = useState("");

    useEffect(() => {
        if (open && initialEmails.length > 0) {
            setEmailText(initialEmails.join(", "));
        }
        // Seed the textarea only on the open transition; re-seeding on every
        // initialEmails identity change would clobber the user's edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const [selectedRole, setSelectedRole] = useState<UserRole>();
    const [selectedKBs, setSelectedKBs] = useState<KBOption[]>([]);
    // Whether the user has attempted an invalid submit — gates the announced
    // (role=alert) validation summary so it doesn't fire before they try (3.3.1).
    const [showErrors, setShowErrors] = useState(false);
    // Base id for associating each visible <label> with its control (WCAG 1.3.1/3.3.2).
    const fieldId = useId();

    const handleCloseClick = () => {
        handleClose();
        resetForm();
    };

    const resetForm = () => {
        setEmailText("");
        setSelectedRole(undefined);
        setSelectedKBs([]);
        setShowErrors(false);
    };

    const validationIssues: string[] = [];
    if (!emailText.trim()) validationIssues.push("At least one email address is required");
    if (selectedKBs.length === 0) validationIssues.push("Select at least one assistant");
    if (!selectedRole) validationIssues.push("Select a role");
    const isValid = validationIssues.length === 0;

    const handleActionClick = () => {
        if (!selectedRole || selectedKBs.length === 0 || !emailText.trim()) {
            setShowErrors(true);
            return;
        }

        const emails = emailText
            .split(/[,\n;]+/)
            .map(e => e.trim().toLowerCase())
            .filter(e => e.length > 0);

        handleAction(emails, selectedKBs, selectedRole);
        resetForm();
    };

    return (
        <ModalTemplate open={open} width={654} handleClose={handleCloseClick} ariaLabel="Add Users">
            <div className="um-add-users-modal">
                <div className="um-add-users-modal-title">
                    <SvgIcon
                        className="um-add-users-modal-title__icon"
                        component={UserIcon}
                        inheritViewBox
                    />
                    <span className="um-add-users-modal-title__text">Add Users</span>
                </div>
                <div className="um-add-users-modal-description">
                    <span className="um-add-users-modal-description__text">
                        Enter email addresses (comma or newline separated) and select assistants to
                        add users to.
                    </span>
                </div>
                <div className="um-add-users-modal-field">
                    <label
                        className="um-add-users-modal-field__label"
                        htmlFor={`${fieldId}-emails`}
                    >
                        Emails
                    </label>
                    <InputBase
                        className="um-add-users-modal-field__input"
                        multiline
                        rows={3}
                        placeholder="user1@example.com, user2@example.com"
                        value={emailText}
                        onChange={e => setEmailText(e.target.value)}
                        inputProps={{ id: `${fieldId}-emails` }}
                    />
                </div>
                <div className="um-add-users-modal-field">
                    {/* Visual caption only; KBMultiSelect names itself via aria-label. */}
                    <span className="um-add-users-modal-field__label">Assistants</span>
                    <KBMultiSelect
                        knowledgeBases={knowledgeBases}
                        selectedKBs={selectedKBs}
                        setSelectedKBs={setSelectedKBs}
                    />
                </div>
                <div className="um-add-users-modal-field">
                    {/* Associated with the control below via SelectionFilter's aria-labelledby. */}
                    <span className="um-add-users-modal-field__label" id={`${fieldId}-role-label`}>
                        Role
                    </span>
                    <SelectionFilter
                        ariaLabelledBy={`${fieldId}-role-label`}
                        list={[UserRole.user, UserRole.manager].map(
                            role => role.charAt(0).toUpperCase() + role.slice(1)
                        )}
                        selectedItem={
                            selectedRole
                                ? selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)
                                : undefined
                        }
                        setSelectedItem={(role: string) => {
                            setSelectedRole(role.toLowerCase() as UserRole);
                        }}
                        placeholder="Select Role"
                        dropdownMenuType={DropdownMenuType.wider}
                        rightPosition={true}
                        minWidth={320}
                    />
                </div>
                {showErrors && !isValid && (
                    <div className="um-add-users-modal-error" role="alert">
                        <ul>
                            {validationIssues.map((issue, i) => (
                                <li key={i}>{issue}</li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="um-add-users-modal-action">
                    <Button className="primary-button" variant="text" onClick={handleActionClick}>
                        Add Users
                    </Button>
                </div>
            </div>
        </ModalTemplate>
    );
};
